import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

// ─── Auth token helpers ────────────────────────────────────────────────────────
const TOKEN_KEY = 'ocym_access_token';

export const setStoredToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp && payload.exp * 1000 < Date.now();
  } catch { return false; }
};

export const getStoredToken = () => {
  const stored = localStorage.getItem(TOKEN_KEY);
  if (stored && isTokenExpired(stored)) {
    localStorage.removeItem(TOKEN_KEY);
    return import.meta.env.VITE_SUPABASE_ANON_KEY;
  }
  return stored || import.meta.env.VITE_SUPABASE_ANON_KEY;
};

// Refresh token if needed
export const refreshStoredToken = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    
    if (session?.access_token) {
      setStoredToken(session.access_token);
      return session.access_token;
    }
  } catch (err) {
    console.error('[refreshStoredToken] Failed:', err.message);
  }
  // No active Supabase session — clear any stale/expired token and use anon key
  localStorage.removeItem(TOKEN_KEY);
  return import.meta.env.VITE_SUPABASE_ANON_KEY;
};

// ─── Generic direct-fetch helpers ─────────────────────────────────────────────
const SUPA_URL = () => import.meta.env.VITE_SUPABASE_URL;
const SUPA_KEY = () => import.meta.env.VITE_SUPABASE_ANON_KEY;

const buildHeaders = (customToken) => ({
  'apikey': SUPA_KEY(),
  'Authorization': `Bearer ${customToken || getStoredToken()}`,
  'Content-Type': 'application/json',
});

const supabaseGet = async (tableName, filters = [], retry = true) => {
  const url = new URL(`${SUPA_URL()}/rest/v1/${tableName}`);
  url.searchParams.set('select', '*');
  const orders = [];
  filters.forEach(([col, op, val]) => {
    if (op === '==')  url.searchParams.append(col, `eq.${val}`);
    else if (op === '!=')  url.searchParams.append(col, `neq.${val}`);
    else if (op === '>')   url.searchParams.append(col, `gt.${val}`);
    else if (op === '>=')  url.searchParams.append(col, `gte.${val}`);
    else if (op === '<')   url.searchParams.append(col, `lt.${val}`);
    else if (op === '<=')  url.searchParams.append(col, `lte.${val}`);
    else if (op === 'in')  url.searchParams.append(col, `in.(${val.join(',')})`);
    else if (op === 'order') orders.push(`${col}.${val === 'desc' ? 'desc' : 'asc'}`);
  });
  if (orders.length) url.searchParams.set('order', orders.join(','));

  const res = await fetch(url.toString(), { headers: buildHeaders() });
  
  // Handle JWT expiration
  if (res.status === 401 && retry) {
    await refreshStoredToken();
    return supabaseGet(tableName, filters, false);
  }
  
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
};

const supabaseGetOne = async (tableName, id, retry = true) => {
  const url = `${SUPA_URL()}/rest/v1/${tableName}?id=eq.${id}&select=*`;
  const res = await fetch(url, { headers: { ...buildHeaders(), 'Accept': 'application/vnd.pgrst.object+json' } });
  if (res.status === 406 || res.status === 404) return null;
  
  // Handle JWT expiration
  if (res.status === 401 && retry) {
    await refreshStoredToken();
    return supabaseGetOne(tableName, id, false);
  }
  
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
};

// ─── Generic real-time collection hook ────────────────────────────────────────
// Mirrors Firebase onSnapshot behaviour using Supabase Realtime
export const useCollection = (tableName, filters = []) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    if (!tableName) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await supabaseGet(tableName, filters);
      setData(rows || []);
    } catch (err) {
      console.error(`[useCollection] Error fetching ${tableName}:`, err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [tableName, JSON.stringify(filters)]);

  return { data, loading, error, refresh: fetchData };
};

// ─── Single document real-time hook ───────────────────────────────────────────
export const useDocument = (tableName, id) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tableName || !id) { setData(null); setLoading(false); return; }
    setLoading(true);
    supabaseGetOne(tableName, id)
      .then(row => setData(row))
      .catch(err => { console.error(`[useDocument] ${tableName}/${id}:`, err.message); setError(err.message); })
      .finally(() => setLoading(false));
  }, [tableName, id]);

  return { data, loading, error };
};

// ─── CRUD helpers ──────────────────────────────────────────────────────────────
export const createDocument = async (tableName, data, retry = true, isPublic = false) => {
  
  const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/${tableName}`;
  
  // For public inserts, use anon key directly
  // For authenticated inserts, use stored token
  let token = getStoredToken();
  if (!token || isPublic) {
    // Use anon key for public access
    token = import.meta.env.VITE_SUPABASE_ANON_KEY;
  } else if (token !== import.meta.env.VITE_SUPABASE_ANON_KEY) {
    // We have a user token, try to refresh it
    const freshToken = await refreshStoredToken();
    if (freshToken) token = freshToken;
  }
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({ ...data, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    // Handle JWT expiration
    if (response.status === 401 && retry) {
      const newToken = await refreshStoredToken();
      if (newToken && newToken !== token) {
        // Retry with new token
        return createDocument(tableName, data, false);
      }
    }
    
    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Insert failed: ${response.status} - ${errText}`);
    }
    
    const row = await response.json();
    return row[0] || row;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new Error('Insert request timed out after 10 seconds');
    }
    throw err;
  }
};

export const updateDocument = async (tableName, id, data, retry = true) => {
  const url = `${SUPA_URL()}/rest/v1/${tableName}?id=eq.${id}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { ...buildHeaders(), 'Prefer': 'return=representation' },
    body: JSON.stringify({ ...data, updated_at: new Date().toISOString() }),
  });
  
  // Handle JWT expiration
  if (res.status === 401 && retry) {
    await refreshStoredToken();
    return updateDocument(tableName, id, data, false);
  }
  
  const body = await res.text();
  if (!res.ok) throw new Error(`Update failed: ${res.status} - ${body}`);
  let rows;
  try { rows = JSON.parse(body); } catch { rows = []; }
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error('Update was blocked — you may not have permission to update this record.');
  }
  return rows[0];
};

export const deleteDocument = async (tableName, id, retry = true) => {
  const url = `${SUPA_URL()}/rest/v1/${tableName}?id=eq.${id}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: { ...buildHeaders(), 'Prefer': 'return=representation' },
  });

  // Handle JWT expiration
  if (res.status === 401 && retry) {
    await refreshStoredToken();
    return deleteDocument(tableName, id, false);
  }

  const body = await res.text();
  if (!res.ok) throw new Error(`Delete failed: ${res.status} - ${body}`);

  // If RLS blocked the delete, Supabase still returns 200 but with an empty array
  let deleted;
  try { deleted = JSON.parse(body); } catch { deleted = []; }
  if (!Array.isArray(deleted) || deleted.length === 0) {
    throw new Error('Delete was blocked — you may not have permission to delete this record.');
  }
  return id;
};

// ─── Donor-specific ───────────────────────────────────────────────────────────

export const getDonorByAuthUid = async (authUid) => {
  if (!authUid) return null;
  const rows = await supabaseGet('donors', [['auth_uid', '==', authUid]]);
  return rows?.[0] || null;
};

const toNoonUTC = (dateInput) => {
  const iso = typeof dateInput === 'string' ? dateInput : dateInput.toISOString();
  const [y, m, d] = iso.split('T')[0].split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
};

export const registerDonor = async (donorData) => {
  if (donorData.lastDonationDate) {
    const lastDate = toNoonUTC(donorData.lastDonationDate);
    const nextDate = new Date(lastDate);
    nextDate.setUTCDate(nextDate.getUTCDate() + 56);
    donorData.next_eligible_date = nextDate.toISOString();
  }
  return await createDocument('donors', {
    name: donorData.name,
    email: donorData.email,
    phone_number: donorData.phoneNumber,
    blood_group: donorData.bloodGroup,
    location: donorData.location,
    district: donorData.district,
    parish: donorData.parish || null,
    age: parseInt(donorData.age) || null,
    weight: parseFloat(donorData.weight) || null,
    last_donation_date: donorData.lastDonationDate ? toNoonUTC(donorData.lastDonationDate).toISOString() : null,
    next_eligible_date: donorData.next_eligible_date || null,
    is_available: donorData.isAvailable !== false,
    total_donations: donorData.totalDonations || 0,
    points: donorData.points || 0,
    achievements: donorData.achievements || [],
    auth_uid: donorData.authUid,
    privacy_settings: donorData.privacySettings || { showPhoneNumber: true, showEmail: false, showLocation: true },
    notification_preferences: donorData.notificationPreferences || {},
    registration_source: donorData.registrationSource || 'direct',
    qr_label: donorData.qrLabel || null,
    qr_source: donorData.qrSource || null,
  });
};

export const getDonorResponsesByRequest = async (requestId) => {
  if (!requestId) return [];
  try {
    const rows = await supabaseGet('donor_responses', [
      ['request_id', '==', requestId],
      ['can_donate', '==', true],
      ['status', '==', 'pending']
    ]);
    return rows || [];
  } catch (err) {
    console.error('Error fetching donor responses:', err);
    return [];
  }
};

export const updateDonorProfile = async (donorId, updates) => {
  const mapped = {};
  if (updates.name !== undefined) mapped.name = updates.name;
  if (updates.phoneNumber !== undefined) mapped.phone_number = updates.phoneNumber;
  if (updates.bloodGroup !== undefined) mapped.blood_group = updates.bloodGroup;
  if (updates.location !== undefined) mapped.location = updates.location;
  if (updates.district !== undefined) mapped.district = updates.district;
  if (updates.parish !== undefined) mapped.parish = updates.parish;
  if (updates.age !== undefined) mapped.age = updates.age;
  if (updates.weight !== undefined) mapped.weight = updates.weight;
  if (updates.isAvailable !== undefined) mapped.is_available = updates.isAvailable;
  if (updates.privacySettings !== undefined) mapped.privacy_settings = updates.privacySettings;
  if (updates.notificationPreferences !== undefined) mapped.notification_preferences = updates.notificationPreferences;
  if (updates.fcmToken !== undefined) mapped.fcm_token = updates.fcmToken;
  if (updates.lastDonationDate) {
    const lastDate = toNoonUTC(updates.lastDonationDate);
    const nextDate = new Date(lastDate);
    nextDate.setUTCDate(nextDate.getUTCDate() + 56);
    mapped.last_donation_date = lastDate.toISOString();
    mapped.next_eligible_date = nextDate.toISOString();
    mapped.total_donations = (updates.totalDonations || 0) + 1;
  }
  return await updateDocument('donors', donorId, mapped);
};

export const toggleDonorStatus = async (donorId, currentStatus) => {
  return await updateDocument('donors', donorId, { is_active: !currentStatus });
};

export const deleteDonor = async (donorId) => {
  return await deleteDocument('donors', donorId);
};

export const useDonationHistory = (donorId) => {
  return useCollection('donation_history', [['donor_id', '==', donorId], ['donation_date', 'order', 'desc']]);
};

// ─── Filtered donors (admin) ──────────────────────────────────────────────────
export const useFilteredDonors = (filters = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const f = [['created_at', 'order', 'desc']];
    if (filters.bloodGroup) f.push(['blood_group', '==', filters.bloodGroup]);
    if (filters.district) f.push(['district', '==', filters.district]);
    if (filters.isAvailable !== undefined) f.push(['is_available', '==', filters.isAvailable]);
    if (filters.parish) f.push(['parish', '==', filters.parish]);
    setLoading(true);
    supabaseGet('donors', f)
      .then(rows => setData(rows || []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [JSON.stringify(filters), refreshTick]);

  const refresh = () => setRefreshTick(t => t + 1);

  return { data, loading, error, refresh };
};

export const createDonor = async (donorData) => {
  return await createDocument('donors', {
    name: donorData.name,
    email: donorData.email || null,
    phone_number: donorData.phoneNumber,
    blood_group: donorData.bloodGroup,
    location: donorData.location,
    district: donorData.district,
    parish: donorData.parish || null,
    age: Number(donorData.age || 0),
    weight: Number(donorData.weight || 0),
    is_available: donorData.isAvailable !== false,
    total_donations: 0,
    points: 0,
    achievements: [],
    privacy_settings: { showPhoneNumber: true, showEmail: false, showLocation: true },
    notification_preferences: { bloodRequests: true, urgentRequests: true, compatibleBloodGroups: true, locationRadius: 50 },
    created_by: donorData.createdBy || null,
    registration_source: 'admin',
  });
};

export const searchDonors = async (filters) => {
  const f = [['is_available', '==', true], ['last_donation_date', 'order', 'asc']];
  if (filters.bloodGroup) f.push(['blood_group', '==', filters.bloodGroup]);
  if (filters.district) f.push(['district', '==', filters.district]);
  if (filters.parish) f.push(['parish', '==', filters.parish]);
  const data = await supabaseGet('donors', f);
  const error = null;
  if (error) throw error;
  return data || [];
};

// ─── Blood requests ───────────────────────────────────────────────────────────
export const createBloodRequest = async (requestData) => {
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 7);
  return await createDocument('blood_requests', {
    patient_name: requestData.patientName,
    blood_group: requestData.bloodGroup,
    units_required: requestData.unitsRequired,
    urgency_level: requestData.urgencyLevel,
    required_by_date: requestData.requiredByDate ? new Date(requestData.requiredByDate).toISOString() : null,
    hospital_name: requestData.hospital?.name,
    hospital_location: requestData.hospital?.location,
    hospital_district: requestData.hospital?.district,
    contact_person_name: requestData.contactPerson?.name,
    contact_person_phone: requestData.contactPerson?.phoneNumber,
    contact_person_relationship: requestData.contactPerson?.relationship,
    requested_by: requestData.requestedBy,
    parish: requestData.parish || null,
    additional_notes: requestData.additionalNotes || '',
    status: 'active',
    expires_at: expiresAt.toISOString(),
    interested_donors: [],
    accepted_donors: [],
  });
};

export const useActiveBloodRequests = (district = null) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const nowIso = new Date().toISOString();
        // Fetch active requests + recently fulfilled (last 7 days) so fulfilled ones show a green banner
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/blood_requests?or=(status.eq.active,and(status.eq.fulfilled,fulfilled_at.gte.${sevenDaysAgo}))&order=created_at.desc`;

        const response = await fetch(url, {
          headers: {
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          }
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch: ${response.status} ${errorText}`);
        }

        const rows = await response.json();
        const now = new Date();

        // Auto-expire: required_by_date passed OR expires_at passed
        const expiredIds = (rows || [])
          .filter(r => {
            if (r.required_by_date) {
              const d = new Date(r.required_by_date);
              d.setHours(23, 59, 59, 999);
              if (d < now) return true;
            }
            if (r.expires_at && new Date(r.expires_at) < now) return true;
            return false;
          })
          .map(r => r.id);

        if (expiredIds.length > 0) {
          // Update expired requests in background
          expiredIds.forEach(async (id) => {
            try {
              await fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/blood_requests?id=eq.${id}`, {
                method: 'PATCH',
                headers: {
                  'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                  'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status: 'expired', updated_at: new Date().toISOString() })
              });
            } catch (e) {
            }
          });
        }

        // Map snake_case database fields to camelCase for components
        const mappedRows = (rows || [])
          .filter(r => {
            // Exclude expired/cancelled but keep fulfilled so they show the green banner
            if (r.status === 'expired' || r.status === 'cancelled') return false;
            // Fulfilled requests always pass through regardless of dates
            if (r.status === 'fulfilled') return true;
            // Exclude active requests if required_by_date has passed (end of that day)
            if (r.required_by_date) {
              const d = new Date(r.required_by_date);
              d.setHours(23, 59, 59, 999);
              if (d < now) return false;
            }
            // Exclude active requests if expires_at has passed
            if (r.expires_at && new Date(r.expires_at) < now) return false;
            return true;
          })
          .map(r => ({
            id: r.id,
            patientName: r.patient_name,
            bloodGroup: r.blood_group,
            unitsRequired: r.units_required,
            urgencyLevel: r.urgency_level,
            requiredByDate: r.required_by_date,
            hospital: {
              name: r.hospital_name,
              location: r.hospital_location,
              district: r.hospital_district,
            },
            contactPerson: {
              name: r.contact_person_name,
              phoneNumber: r.contact_person_phone,
              relationship: r.contact_person_relationship,
            },
            parish: r.parish,
            additionalNotes: r.additional_notes,
            status: r.status,
            createdAt: r.created_at,
            expiresAt: r.expires_at,
            interestedDonors: r.interested_donors || [],
            acceptedDonors: r.accepted_donors || [],
            fulfilledAt: r.fulfilled_at,
            fulfilledBy: r.fulfilled_by,
          }));
        setData(mappedRows);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, [district]);

  return { data, loading, error };
};

export const useMyBloodRequests = (requestedBy) => {
  return useCollection('blood_requests', requestedBy
    ? [['requested_by', '==', requestedBy], ['created_at', 'order', 'desc']]
    : []);
};

export const expressInterest = async (requestId, donorId) => {
  const { data: req, error: fetchErr } = await supabase
    .from('blood_requests').select('interested_donors').eq('id', requestId).single();
  if (fetchErr) throw fetchErr;
  const donors = req.interested_donors || [];
  if (!donors.includes(donorId)) {
    donors.push(donorId);
    const { error } = await supabase.from('blood_requests')
      .update({ interested_donors: donors, updated_at: new Date().toISOString() })
      .eq('id', requestId);
    if (error) throw error;
  }
  return true;
};

export const acceptBloodRequest = async (requestId, donorInfo) => {
  const { data: req, error: fetchErr } = await supabase
    .from('blood_requests').select('status, accepted_donors').eq('id', requestId).single();
  if (fetchErr) throw fetchErr;
  if (req.status !== 'active') throw new Error('This blood request is no longer active');
  const accepted = req.accepted_donors || [];
  accepted.push({ ...donorInfo, accepted_at: new Date().toISOString() });
  const { error } = await supabase.from('blood_requests')
    .update({ accepted_donors: accepted, updated_at: new Date().toISOString() })
    .eq('id', requestId);
  if (error) throw error;
  return { success: true };
};

export const fulfillBloodRequest = async (requestId, donorIds) => {
  const donationDate = new Date().toISOString();
  const nextEligibleDate = new Date();
  nextEligibleDate.setDate(nextEligibleDate.getDate() + 56);

  const { error: reqErr } = await supabase.from('blood_requests')
    .update({ status: 'fulfilled', fulfilled_at: donationDate, fulfilled_by: donorIds, updated_at: donationDate })
    .eq('id', requestId);
  if (reqErr) throw reqErr;

  for (const donorId of donorIds) {
    await createDocument('donation_history', {
      donor_id: donorId,
      request_id: requestId,
      donation_date: donationDate,
    });
    const { data: donor } = await supabase.from('donors').select('total_donations').eq('id', donorId).single();
    await updateDocument('donors', donorId, {
      last_donation_date: donationDate,
      next_eligible_date: nextEligibleDate.toISOString(),
      total_donations: (donor?.total_donations || 0) + 1,
    });
  }
  return true;
};

// ─── Admin functions ──────────────────────────────────────────────────────────
export const getAdminByEmail = async (email) => {
  const rows = await supabaseGet('admins', [
    ['email', '==', email.toLowerCase()],
    ['is_active', '==', true]
  ]);
  return rows?.[0] || null;
};

export const updateAdminLastLogin = async (adminId) => {
  return await updateDocument('admins', adminId, { last_login_at: new Date().toISOString() });
};

export const createAdmin = async (adminData) => {
  return await createDocument('admins', {
    email: adminData.email?.toLowerCase(),
    name: adminData.name,
    role: adminData.role,
    permissions: adminData.permissions || [],
    allowed_locations: adminData.allowedLocations || [],
    is_active: true,
    created_by: adminData.createdBy || null,
    last_login_at: null,
  });
};

export const useAdmins = () => {
  return useCollection('admins', [['created_at', 'order', 'desc']]);
};

export const updateAdminRole = async (adminId, roleData) => {
  return await updateDocument('admins', adminId, {
    role: roleData.role,
    permissions: roleData.permissions,
    allowed_locations: roleData.allowedLocations,
  });
};

export const promoteDonorToAdmin = async (donorId, adminData) => {
  const donor = await supabaseGetOne('donors', donorId);
  if (!donor) throw new Error('Donor not found');

  return await createDocument('admins', {
    email: donor.email?.toLowerCase(),
    name: donor.name,
    phone_number: donor.phone_number,
    location: donor.location,
    district: donor.district,
    original_donor_id: donorId,
    role: adminData.role,
    permissions: adminData.permissions || [],
    allowed_locations: adminData.allowedLocations || [],
    is_active: true,
    created_by: adminData.createdBy || null,
  });
};

export const logContactAttempt = async (donorId, contactData) => {
  return await createDocument('contact_logs', {
    donor_id: donorId,
    method: contactData.method,
    message: contactData.message || null,
    subject: contactData.subject || null,
    urgency: contactData.urgency || 'normal',
    contacted_by: contactData.contactedBy || null,
  });
};

export const bulkContactDonors = async (donorIds, contactData) => {
  const results = [];
  for (const id of donorIds) {
    try {
      const result = await logContactAttempt(id, contactData);
      results.push(result);
    } catch (e) {
      console.error(`Failed to log contact for donor ${id}:`, e);
    }
  }
  return results;
};

// ─── Parishes ─────────────────────────────────────────────────────────────────
export const useParishes = () => {
  return useCollection('parishes', [['name', 'order', 'asc']]);
};

export const addParish = async (parishData) => {
  return await createDocument('parishes', parishData);
};

// ─── Compatible donors hook ───────────────────────────────────────────────────
export const useCompatibleDonors = (bloodGroup, district) => {
  const filters = [['is_available', '==', true]];
  if (bloodGroup) filters.push(['blood_group', '==', bloodGroup]);
  if (district) filters.push(['district', '==', district]);
  return useCollection('donors', filters);
};

// ─── Donations / leaderboard ──────────────────────────────────────────────────
export const recordBloodDonation = async (donorId, donationData) => {
  const record = await createDocument('donation_history', {
    donor_id: donorId,
    hospital: donationData.hospital || null,
    donation_date: donationData.donationDate ? toNoonUTC(donationData.donationDate).toISOString() : new Date().toISOString(),
    blood_group: donationData.donorBloodGroup || null,
    units: donationData.units || 1,
    notes: donationData.notes || '',
    recorded_by: donationData.recordedBy || null,
    request_id: donationData.requestId || null,
  });
  const { data: donor } = await supabase.from('donors').select('total_donations, points').eq('id', donorId).single();
  const pointsEarned = 100;
  const lastDate = toNoonUTC(record.donation_date);
  const nextDate = new Date(lastDate);
  nextDate.setUTCDate(nextDate.getUTCDate() + 56);
  await updateDocument('donors', donorId, {
    total_donations: (donor?.total_donations || 0) + 1,
    points: (donor?.points || 0) + pointsEarned,
    last_donation_date: lastDate.toISOString(),
    next_eligible_date: nextDate.toISOString(),
  });
  return { ...record, pointsEarned };
};

export const awardCongratulations = async (donorId, data) => {
  return await createDocument('congratulations', {
    donor_id: donorId,
    donor_name: data.donorName || null,
    message: data.message || '',
    type: data.type || 'general',
    awarded_by: data.awardedBy || null,
  });
};

export const useLeaderboard = (limitCount = 50) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('donors')
      .select('id, name, blood_group, district, total_donations, points, achievements')
      .order('total_donations', { ascending: false })
      .limit(limitCount)
      .then(({ data: rows }) => { setData(rows || []); setLoading(false); });
  }, [limitCount]);

  return { data, loading };
};

export const useRecentDonations = (limitCount = 20) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('donation_history')
      .select('*')
      .order('donation_date', { ascending: false })
      .limit(limitCount)
      .then(({ data: rows }) => { setData(rows || []); setLoading(false); });
  }, [limitCount]);

  return { data, loading };
};

export const useDonorCongratulations = (donorId) => {
  return useCollection('congratulations', donorId ? [['donor_id', '==', donorId]] : []);
};
